import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Formik, Form } from 'formik'

import useHTTPRequest, { RequestStatus } from '@hooks/useHTTPRequest'
import useModalVisibilityControls from "@hooks/useModalVisibilityControls"
import { Modal, ModalHeader, ModalSubHeader, ModalContent } from '@components/Modal'
import { Button } from "@components/Button"
import { InputFieldWithLabel } from '@components/Form/Fields'
import { ReactComponent as ManRelaxingImage } from '@assets/images/illustrations/relax.svg'
import { ReactComponent as SupportBubbleClickInstructionImage } from '@assets/images/illustrations/support-bubble-click-instruction.svg'

import './quick-report-asset-issue.scss'

export const useQuickReportAssetIssue = ({ tenantSlug, client_id, profile_id }) => {
  const modalControls = useModalVisibilityControls()
  const [selectedAsset, setSelectedAsset] = useState(undefined)
  const showQuickReportModalForAsset = (asset) => {
    setSelectedAsset(asset)
    modalControls.openModal()
  }
  const modal = selectedAsset !== undefined ? (
    <QuickReportAssetIssueModal
      open={modalControls.modalVisible}
      onClose={modalControls.closeModal}
      asset={{ id: selectedAsset.id, name: selectedAsset.name, contractNumber: selectedAsset.contract_number }}
      tenantSlug={tenantSlug}
      clientId={client_id}
      wealthManagerId={profile_id} />
  ) : null

  return {
    modal,
    showQuickReportModalForAsset
  }
}

export const QuickReportAssetIssueModal = ({
  open,
  onClose,
  asset,
  clientId,
  wealthManagerId,
}) => {
  const request = useHTTPRequest({
    url: `/api/customer-support/asset-quick-report`,
    method: 'POST'
  })
  const closeModalAndResetRequest = () => {
    request.reset({ resetState: false })
    onClose()
  }
  const handleSubmit = (values) => {
    const payload = {
      client_id: clientId,
      wealth_manager_id: wealthManagerId,
      asset_id: asset.id,
      ...values
    }
    request.execute(payload)
  }
  const assetName = asset.contractNumber ? `${asset.name} (${asset.contractNumber})` : asset.name
  let ContentComponent
  switch (request.status) {
    case RequestStatus.success:
      ContentComponent = SuccessContent
      break
    case RequestStatus.error:
      ContentComponent = ErrorContent
      break
    default:
      ContentComponent = FormContent
  }

  return (
    <Modal open={open} onClose={closeModalAndResetRequest} className='quick-report-asset-issue-modal'>
      <ContentComponent
        onClose={closeModalAndResetRequest}
        assetName={assetName}
        loading={request.loading}
        handleSubmit={handleSubmit} />
    </Modal>
  )
}

QuickReportAssetIssueModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  tenantSlug: PropTypes.string.isRequired,
  clientId: PropTypes.number.isRequired,
  wealthManagerId: PropTypes.number.isRequired,
  asset: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    contractNumber: PropTypes.string
  }).isRequired,
}

const FormContent = ({ onClose, loading, handleSubmit, assetName }) => <>
  <ModalHeader>Quick Report an Asset Issue</ModalHeader>
  <ModalSubHeader>
    Submit a support request about this asset
  </ModalSubHeader>
  <ModalContent>
    <Formik onSubmit={handleSubmit} initialValues={{ description: '' }}>
      {() =>
        <Form className='app-form quick-report-asset-issue-modal__form'>
          <div className='asset-fields-container'>
            <p><strong>Asset name: </strong>{assetName}</p>
          </div>
          <InputFieldWithLabel
            autoFocus
            as='textarea'
            rows={2}
            label='Describe the problem'
            name='description' />

          <div className='quick-report-asset-issue-modal__form-actions'>
            <Button secondary disabled={loading} onClick={onClose}>Cancel</Button>
            <Button primary disabled={loading} type='submit'>
              {loading ? 'Loading...' : 'Submit'}
            </Button>
          </div>
        </Form>
      }
    </Formik>
  </ModalContent>
</>

const SuccessContent = ({ onClose }) => <>
  <ModalHeader>Success!</ModalHeader>
  <ModalContent className='quick-report-asset-issue-modal__success-content'>
    <p>We’ve submitted your support request and will be in touch soon.</p>
    <ManRelaxingImage alt='Person relaxing on a sofa' style={{ marginBlockEnd: '2rem' }} />
    <Button primary onClick={onClose}>Done</Button>
  </ModalContent>
</>

const ErrorContent = ({ onClose }) => <>
  <ModalHeader>Something went wrong</ModalHeader>
  <ModalSubHeader>An error occurred submitting your request</ModalSubHeader>
  <ModalContent className='quick-report-asset-issue-modal__error-content'>
    <SupportBubbleClickInstructionImage alt='Customer Support button floating in the bottom right of a webpage' />
    <p>You can still submit a support request by clicking on the icon floating in the bottom right of the page.</p>
    <Button primary onClick={onClose}>Close</Button>
    <p style={{ lineHeight: '1.1' }}><small>* If this doesn’t work, it means we’re experiencing an issue with our support provider and you can reach out directly to us at <a href='mailto:feedback@wealthbit.co'>feedback@wealthbit.co</a></small></p>
  </ModalContent>
</>
